import React, { useEffect, useMemo, useRef } from 'react';
import FuriganaText from './FuriganaText';

function AttentionGuide({ from, to, message, animationSpeed = 1, animationKey = 'default', paused = false }) {
  const pathRef = useRef(null);

  const pathD = useMemo(() => {
    if (!from || !to) return '';
    const fromX = from.x + from.width / 2;
    const fromY = from.y + from.height / 2;
    const toX = to.x + to.width / 2;
    const toY = to.y + to.height / 2;
    const midX = (fromX + toX) / 2;
    const midY = Math.min(fromY, toY) - 60;
    return `M ${fromX} ${fromY} Q ${midX} ${midY} ${toX} ${toY}`;
  }, [from, to]);

  // Base animation duration is 800ms, adjusted by speed multiplier
  const animationDuration = useMemo(() => {
    const speed = Math.max(0.5, Math.min(2.5, animationSpeed));
    return Math.round(800 / speed);
  }, [animationSpeed]);

  useEffect(() => {
    const node = pathRef.current;
    if (!node || !node.getTotalLength) return;
    const length = node.getTotalLength();
    node.style.strokeDasharray = `${length}`;
    node.style.strokeDashoffset = paused ? '0' : `${length}`;
    try {
      node.getBoundingClientRect(); // force layout
      if (paused) {
        node.style.transition = 'none';
        return;
      }
      node.style.transition = `stroke-dashoffset ${animationDuration}ms ease-out`;
      node.style.strokeDashoffset = '0';
    } catch (e) {
      // ignore
    }
  }, [animationDuration, animationKey, pathD, paused]);

  if (!pathD) return null;

  const tipX = to ? Math.max(to.x - 8, 0) : 0;
  const tipY = to ? Math.max(to.y - 52, 0) : 0;

  return (
    <svg className="absolute inset-0 pointer-events-none z-50" aria-hidden="true">
      <defs>
        <marker id="attention-arrowhead" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
          <polygon points="0 0, 10 3.5, 0 7" fill="var(--danger)" />
        </marker>
        <filter id="attention-shadow" x="-40%" y="-40%" width="180%" height="180%">
          <feDropShadow dx="0" dy="2" stdDeviation="3" floodColor="#000000" floodOpacity="0.22" />
        </filter>
      </defs>

      <path
        ref={pathRef}
        d={pathD}
        stroke="var(--danger)"
        strokeWidth="3"
        fill="none"
        markerEnd="url(#attention-arrowhead)"
        filter="url(#attention-shadow)"
      />

      {message && (
        <foreignObject x={tipX} y={tipY} width="240" height="64">
          <div className="rounded-2xl px-4 py-3 text-xs" style={{ backgroundColor: 'var(--text)', color: 'var(--text-on-primary)', boxShadow: 'var(--shadow-lg)', opacity: 0.9 }}>
            <FuriganaText text={message} />
          </div>
        </foreignObject>
      )}
    </svg>
  );
}

export default AttentionGuide;
